import type { LenisOptions } from 'lenis'
import { easeInOutCubicFn } from './easing'

interface ScrollToOptions {
  duration?: number
  offset?: number
  easing?: LenisOptions['easing']
}

/**
 * Scroll to target
 */
export function scrollTo(target: number | string | HTMLElement, options: ScrollToOptions = {}) {
  if (window.lenis) {
    window.lenis.scrollTo(target, {
      offset: options.offset ?? -(window.$store?.ui.scrollPaddingTop ?? 0),
      easing: options.easing ?? easeInOutCubicFn,
      duration: options.duration,
    })
  }
  else {
    if (typeof target === 'number') {
      window.scrollTo({ top: target, behavior: 'smooth' })
    }
    else if (typeof target === 'string') {
      const element = document.querySelector(target)
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' })
      }
    }
    else {
      target.scrollIntoView({ behavior: 'smooth' })
    }
  }
}

export interface ScrollToAnchorOptions {
  pushState?: boolean // Push state to history
  blacklist?: string[] // Blacklist of anchors (e.g. ['#', '#close'])
}

/**
 * Scroll to anchor
 */
export function scrollToAnchor(
  element: Element,
  event?: MouseEvent,
  options?: ScrollToAnchorOptions,
) {
  const pushState = options?.pushState ?? true
  const blacklist = ['#', ...(options?.blacklist ?? [])]

  let hash = element.getAttribute('href')
  if (hash?.startsWith('/')) {
    hash = hash.replace('/', '')
  }

  if (hash?.startsWith('#') && !blacklist.includes(hash)) {
    const element = document.querySelector<HTMLElement>(hash)
    if (element) {
      event?.preventDefault()
      scrollTo(element)
      if (pushState) {
        history.pushState(undefined, '', hash)
      }
    }
  }
}

/**
 * Scroll to the next section
 */
export function scrollToNextSection(element: Element) {
  if (element && element.nextElementSibling) {
    scrollTo(element.nextElementSibling as HTMLElement)
  }
}

export default { scrollTo, scrollToAnchor, scrollToNextSection }
